import React, { useRef } from 'react';
import { formatNumber } from '../../utils/number-util';

interface CarCountInputProps {
  value: number | string | null | undefined;
  onChange: (value: number | '') => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

// number-util is untyped JS whose `digits` argument is optional at runtime but
// reads as required once inferred, so it is passed through explicitly.
const withThousands = (digits: string): string => formatNumber(digits, undefined);

/**
 * A car count, written the way people read it: 10,000 rather than 10000.
 *
 * It has to be a text input -- a number input rejects the separators -- so the
 * digits are formatted for display and handed back to the caller as a plain
 * number, never a string with commas in it.
 */
const CarCountInput: React.FC<CarCountInputProps> = ({
  value,
  onChange,
  onBlur,
  name,
  id,
  className = 'form-control',
  placeholder,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const raw = value === null || value === undefined || value === '' ? '' : String(value);
  const display = raw === '' ? '' : withThousands(digitsOnly(raw));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;
    // Commas shift everything right of them, so the caret is restored by how
    // many digits precede it rather than by character offset.
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = digitsOnly(input.value.slice(0, caret)).length;

    const digits = digitsOnly(input.value);
    onChange(digits === '' ? '' : Number(digits));

    if (digitsBeforeCaret === 0 && digits === '') return;
    const formatted = withThousands(digits);
    let seen = 0;
    let position = formatted.length;
    for (let i = 0; i < formatted.length; i += 1) {
      if (/\d/.test(formatted[i])) {
        seen += 1;
        if (seen === digitsBeforeCaret) {
          position = i + 1;
          break;
        }
      }
    }
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(position, position);
    });
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      name={name}
      id={id}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      value={display}
      onChange={handleChange}
      onBlur={onBlur}
    />
  );
};

export default CarCountInput;
